import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import { ErrorProvider, useError } from './contexts/ErrorContext';
import { setGlobalErrorHandler } from './services/api';
import HomePage from './pages/HomePage';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import AllVideosPage from './pages/AllVideosPage';
import PublicGallery from './pages/PublicGallery';
import PublicGalleryLoginView from './pages/PublicGalleryLoginView';
import { authService } from './services/authService';
import EmbedPlayer from './components/EmbedPlayer';
import VideoDetailsPage from './pages/VideoDetailsPage'; 
import PixelTutorPage from './pages/PixelTutorPage';
import ContentCreator from './pages/ContentCreator';
import PixelAI from './pages/PixelAI';
import StudioLanding from './pages/studio/StudioLanding';
import TeacherDashboard from './pages/studio/TeacherDashboard';
import UploadSyllabus from './pages/studio/UploadSyllabus';
import CourseEditor from './pages/studio/CourseEditor';
import AssignmentManager from './pages/studio/AssignmentManager';
import AssignmentResults from './pages/studio/AssignmentResults';
import ClassManagement from './pages/studio/ClassManagement';
import StudentDashboard from './pages/studio/StudentDashboard';
import StudentCourseView from './pages/studio/StudentCourseView';
import ConceptLearning from './pages/studio/ConceptLearning';
import TakeAssignment from './pages/studio/TakeAssignment';
import StudentRoster from './pages/studio/StudentRoster';
import AdaptivePractice from './pages/studio/AdaptivePractice';
import Settings from './pages/Settings';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import './App.css';
import './pages/HomePage.css';
import './pages/Auth.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  if (!authService.isAuthenticated()) {
    return <Navigate to="/signin" replace />;
  }
  return children;
};

// Inner App Content - needs to be inside ErrorProvider to access useError
const AppContent = () => {
  const { showError } = useError();

  useEffect(() => {
    // Set the global error handler for api.js interceptor
    setGlobalErrorHandler(showError);
  }, [showError]);

  return (
    <LanguageProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/gallery" element={<PublicGallery />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/embed/:videoId" element={<EmbedPlayer />} />
          <Route path="/video/:videoId" element={<VideoDetailsPage />} />
          
          {/* Dashboard Route */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          
          {/* QuestionHistory route - FIXED: Points to Dashboard */}
          <Route
            path="/QuestionHistory"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          
          {/* All Videos Page Route */}
          <Route 
            path="/videos" 
            element={
              <ProtectedRoute>
                <AllVideosPage />
              </ProtectedRoute>
            } 
          />	
		  
		  <Route 
            path="/pixel-ai" 
            element={
              <ProtectedRoute>
                <PixelAI />
              </ProtectedRoute>
            } 
          />	
		  
          <Route 
            path="/pgallery" 
            element={
              <ProtectedRoute>
                <PublicGalleryLoginView />
              </ProtectedRoute>
            } 
          />	
          
          <Route 
            path="/content-creator" 
            element={
              <ProtectedRoute>
                <ContentCreator />
              </ProtectedRoute>
            } 
          />	
          
          <Route 
            path="/pixel-tutor" 
            element={
              <ProtectedRoute>
                <PixelTutorPage />
              </ProtectedRoute>
            } 
          />			
          
          {/* AnimLearn Studio Routes */}
          <Route path="/studio" element={<ProtectedRoute><StudioLanding /></ProtectedRoute>} />
          <Route path="/studio/teacher" element={<ProtectedRoute><TeacherDashboard /></ProtectedRoute>} />
          <Route path="/studio/teacher/upload" element={<ProtectedRoute><UploadSyllabus /></ProtectedRoute>} />
          <Route path="/studio/teacher/courses/:courseId" element={<ProtectedRoute><CourseEditor /></ProtectedRoute>} />
          <Route path="/studio/teacher/assignments" element={<ProtectedRoute><AssignmentManager /></ProtectedRoute>} />
          <Route path="/studio/teacher/assignments/:assignmentId/results" element={<ProtectedRoute><AssignmentResults /></ProtectedRoute>} />
          <Route path="/studio/teacher/classes" element={<ProtectedRoute><ClassManagement /></ProtectedRoute>} />
          <Route path="/studio/teacher/students" element={<ProtectedRoute><StudentRoster /></ProtectedRoute>} />
          <Route path="/studio/student" element={<ProtectedRoute><StudentDashboard /></ProtectedRoute>} />
          <Route path="/studio/student/courses/:courseId" element={<ProtectedRoute><StudentCourseView /></ProtectedRoute>} />
          <Route path="/studio/student/concepts/:conceptId" element={<ProtectedRoute><ConceptLearning /></ProtectedRoute>} />
          <Route path="/studio/student/assignments/:assignmentId" element={<ProtectedRoute><TakeAssignment /></ProtectedRoute>} />
          <Route path="/studio/student/adaptive/:sessionId" element={<ProtectedRoute><AdaptivePractice /></ProtectedRoute>} />

          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </LanguageProvider>
  );
};

// Main App Component - wraps everything with ErrorProvider
function App() {
  return (
    <ErrorProvider>
      <AppContent />
    </ErrorProvider>
  );
}

export default App;